import { Account } from "@services/account/types";
import { Device } from "@services/devices/types";
import { Location } from "@services/locations/types";
import { Room } from "@services/rooms/types";
import API from "@services/root/rootApi";
import { SettingsPropertyGroup } from "./types";

export type AssetTypes = "account" | "device" | "location" | "room";

export const getSettings = async <T>(
  group: SettingsPropertyGroup,
  asset?: `/${AssetTypes}/${string}`
): Promise<T> =>
  (await API.get<T>(`/symphony-api/settings${asset ?? ""}?group=${group}`))
    .data;

type PropertiesGetter<T> = ((asset: AssetTypes, id: string) => Promise<T>) & {
  global: () => Promise<T>;
  account: (id: Account["id"]) => Promise<T>;
  device: (id: Device["id"]) => Promise<T>;
  location: (id: Location["id"]) => Promise<T>;
  room: (id: Room["id"]) => Promise<T>;
};
type PropertiesPutter<T, R = unknown> = ((
  data: T,
  asset: AssetTypes,
  id: string
) => Promise<R>) & {
  global: (data: T) => Promise<R>;
  account: (data: T, id: Account["id"]) => Promise<R>;
  device: (data: T, id: Device["id"]) => Promise<R>;
  location: (data: T, id: Location["id"]) => Promise<R>;
  room: (data: T, id: Room["id"]) => Promise<R>;
};
type GetFn<T> = (asset?: `/${AssetTypes}/${string}`) => Promise<T>;
type PutFn<T> = (
  data: T,
  asset?: `/${AssetTypes}/${string}`
) => Promise<unknown>;

export const putSettings = async (
  data: any,
  group: SettingsPropertyGroup,
  asset?: `/${AssetTypes}/${string}`
) =>
  (await API.put(`/symphony-api/settings${asset ?? ""}?group=${group}`, data))
    .data;

export const getBuilder = <T>(fn: GetFn<T>): PropertiesGetter<T> => {
  const builder = async (asset: AssetTypes, id: string) =>
    await fn(`/${asset}/${id}`);
  builder.global = async () => await fn();
  builder.account = async (id: Account["id"]) => await fn(`/account/${id}`);
  builder.device = async (id: Device["id"]) => await fn(`/device/${id}`);
  builder.location = async (id: Location["id"]) => await fn(`/location/${id}`);
  builder.room = async (id: Room["id"]) => await fn(`/room/${id}`);
  return builder;
};
export const putBuilder = <T>(fn: PutFn<T>): PropertiesPutter<T> => {
  const builder = async (data: T, asset: AssetTypes, id: string) =>
    await fn(data, `/${asset}/${id}`);
  builder.global = async (data: T) => await fn(data);
  builder.account = async (data: T, id: Account["id"]) =>
    await fn(data, `/account/${id}`);
  builder.device = async (data: T, id: Device["id"]) =>
    await fn(data, `/device/${id}`);
  builder.location = async (data: T, id: Location["id"]) =>
    await fn(data, `/location/${id}`);
  builder.room = async (data: T, id: Room["id"]) =>
    await fn(data, `/room/${id}`);
  return builder;
};
